import React, { useState } from "react";
import { useNavigate } from "react-router";

import EditProject from "./EditProject";
import RequestCompleteNotice from "../../../../common/settings-modal/RequestCompleteNotice";
import { SonicTermData } from "../../../../common/classes/SonicTerm";

import { useAppSelector, useAppDispatch } from "../../../../redux/hooks";
import { getAuthToken } from "../../../../redux/reducers/auth";
import {
  getRoles,
  getLinkedPages,
  updateLinkedPages,
} from "../../../../redux/reducers/user";

import { RequestState } from "../../../../utils/RequestState";
import {
  FieldState,
  valFileTotalSize,
} from "../../../../utils/ValidationFunctions";
import { useUpdateProject } from "../../../../utils/useProject";
import { TermType, useGetTerm } from "../../../../utils/useTerm";
import { getTermOptions } from "../../../../utils/GeneralUtils";
import {
  fieldStateToUpdateArgs,
  validFieldState,
} from "../FieldStateFunctions";

const CreateProjectContainer: React.FC = () => {
  const [fieldState, setFieldState] = useState<FieldState>();
  const [imgItems, setImgItems] = useState<Array<File>>([]);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [requestState, setRequestState] = useState<RequestState>(
    RequestState.Waiting
  );

  const auth_token = useAppSelector(getAuthToken);
  const linked_pages = useAppSelector(getLinkedPages);
  const roles = useAppSelector(getRoles);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const projectTypes = useGetTerm(TermType.ProjectType, undefined, -1).termData
    .entries;

  const { mutateAsync } = useUpdateProject([
    "project",
    {
      auth_token,
      per: -1,
      id: linked_pages?.project ?? null,
    },
  ]);

  const completeHandler = () => {
    navigate("/mypage/project/settings", { replace: true });
  };

  const handleSubmit = async () => {
    if (
      !validFieldState(
        "create",
        ["sonic_name", "sonic_type", "sonic_start_date"],
        fieldState
      )
    ) {
      setError(true);
      return;
    }

    // 送信する画像の合計サイズを確認
    if (!valFileTotalSize(imgItems)) {
      setError(true);
      return;
    }

    setError(false);
    setRequestState(RequestState.Doing);

    const update_args: { [key: string]: string | File } = {};

    fieldStateToUpdateArgs(update_args, fieldState);

    if (imgItems) {
      imgItems.forEach((v, i) => {
        if (v instanceof File) {
          update_args[`sonic_imgs${i}`] = v;
        } else {
          update_args[`sonic_imgs${i}`] = "";
        }
      });
    }

    if (games && games.length > 0) {
      const gameIds: Array<string> = [];
      games.forEach((game) => gameIds.push(game.id));
      update_args["sonic_game"] = JSON.stringify(gameIds);
    }

    if (auth_token) {
      const res = await mutateAsync({ auth_token, update: update_args });

      if (res) dispatch(updateLinkedPages({ type: "project", id: res.id }));
      setIsComplete(true);
    }
  };

  const handleInputChange = (
    value: File | string | undefined,
    name: string
  ) => {
    let f: FieldState = {};
    if (fieldState !== undefined) {
      f = fieldState;
    }

    if (value !== undefined) {
      if (value instanceof File) {
        setImgItems([...imgItems, value]);
      } else {
        f[name] = [value, true];
      }
      setRequestState(RequestState.Pending);
      setError(false);
    } else {
      if (name !== "sonic_imgs") f[name] = ["", false];
    }
    setFieldState(f);
  };

  const deleteImgItem = (img: File, i: number) => {
    const newList = imgItems.filter((v, index) => index !== i);
    setImgItems(newList);
  };

  // MyGame
  const [games, setGames] = useState<Array<SonicTermData>>([]);

  const handleGames = (value: Array<SonicTermData>) => {
    setGames([...value]);
    setRequestState(RequestState.Pending);
  };

  if (isComplete) {
    return (
      <RequestCompleteNotice
        handler={completeHandler}
        backBarText="プロジェクト設定"
        title="公開申請完了"
        text={
          "公開申請が完了しました。\nこれより審査を開始します。\n結果通知までしばらくお待ちください。"
        }
        sub_text="※内容によっては数日お時間をいただく可能性がございます。"
      />
    );
  } else {
    return (
      <EditProject
        projectTypes={getTermOptions(projectTypes)}
        imgItems={imgItems}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        deleteImgItem={deleteImgItem}
        isHasChanges={fieldState !== undefined ? true : false}
        error={error}
        isStudent={
          roles?.includes("jikei_admin") ||
          roles?.includes("jikei_manager") ||
          roles?.includes("jikei_student")
            ? true
            : false
        }
        requestState={requestState}
        games={games}
        handleGames={handleGames}
      />
    );
  }
};

export default CreateProjectContainer;
